import { forwardRef, type InputHTMLAttributes, type ReactElement } from 'react';

import { cn } from '../lib/cn.js';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /**
   * Marca o input em estado de erro: borda destructive + ring vermelho no
   * focus + `aria-invalid="true"`. Controlado pelo formulário pai.
   */
  hasError?: boolean;
}

/**
 * Input base do design system.
 *
 * Tamanho 40px (comfortable). Font-size 16px em mobile (evita zoom do iOS
 * ao focar) e 14px em desktop (md breakpoint). Suporta todas as props
 * nativas + `hasError` para integração com formulários.
 *
 * @example
 * <Input type="email" inputMode="email" autoComplete="email" placeholder="seu@email.com" />
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, type = 'text', hasError, ...props },
  ref,
): ReactElement {
  return (
    <input
      type={type}
      ref={ref}
      aria-invalid={hasError === true ? true : undefined}
      className={cn(
        'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background md:text-sm',
        'file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
        'placeholder:text-muted-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        hasError === true && 'border-destructive focus-visible:ring-destructive',
        className,
      )}
      {...props}
    />
  );
});
